// NotificationCenter.tsx - Multi-channel notification management
import React, { useState } from 'react';
import {
  Card,
  Text,
  Button,
  Stack,
  Group,
  Badge,
  Tabs,
  Table,
  ActionIcon,
  Tooltip,
  Alert,
  Switch,
  TextInput,
  Textarea,
  Modal,
  Select,
  Progress,
} from '@mantine/core';
import {
  IconBell,
  IconMail,
  IconMessage,
  IconBellRinging,
  IconSend,
  IconCheck,
  IconX,
  IconSettings,
  IconChartBar,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications as mantineNotifications } from '@mantine/notifications';

interface NotificationLog {
  id: string;
  type: 'email' | 'sms' | 'push';
  recipient: string;
  subject?: string;
  message?: string;
  title?: string;
  status: 'sent' | 'failed' | 'pending';
  error?: string;
  sentAt?: string;
  createdAt: string;
}

interface NotificationStats {
  email: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
    bounced: number;
  };
  sms: {
    total: number;
    sent: number;
    failed: number;
    delivered: number;
    pending: number;
  };
  push: {
    totalSent: number;
    totalFailed: number;
    activeSubscriptions: number;
  };
}

export function NotificationCenter() {
  const [sendModalOpened, setSendModalOpened] = useState(false);
  const [notificationType, setNotificationType] = useState<'email' | 'sms' | 'push'>('email');
  const queryClient = useQueryClient();

  // Fetch notification stats
  const { data: statsData } = useQuery({
    queryKey: ['notification-stats'],
    queryFn: async () => {
      const response = await fetch('/api/notifications/stats', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
  });

  const stats: NotificationStats = statsData?.data;

  // Fetch notification logs
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['notification-logs'],
    queryFn: async () => {
      const response = await fetch('/api/notifications/logs?type=all&limit=50', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.json();
    },
  });

  const logs = logsData?.data || {};

  // Test notification mutation
  const testMutation = useMutation({
    mutationFn: async (type: string) => {
      const response = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ type }),
      });
      return response.json();
    },
    onSuccess: () => {
      mantineNotifications.show({
        title: 'Test Notification Sent',
        message: 'Check your inbox/phone for the test notification',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['notification-logs'] });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  return (
    <>
      <Stack gap="md">
        {/* Statistics Cards */}
        {stats && (
          <Group grow>
            <Card withBorder padding="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <IconMail size={24} color="blue" />
                  <Badge color="blue">Email</Badge>
                </Group>
                <Text size="xl" fw={700}>
                  {stats.email.total}
                </Text>
                <Text size="xs" c="dimmed">
                  Total Emails
                </Text>
                <Progress.Root size="sm">
                  <Progress.Section value={(stats.email.sent / stats.email.total) * 100} color="green">
                    <Progress.Label>Sent: {stats.email.sent}</Progress.Label>
                  </Progress.Section>
                  <Progress.Section value={(stats.email.failed / stats.email.total) * 100} color="red">
                    <Progress.Label>Failed: {stats.email.failed}</Progress.Label>
                  </Progress.Section>
                </Progress.Root>
              </Stack>
            </Card>

            <Card withBorder padding="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <IconMessage size={24} color="teal" />
                  <Badge color="teal">SMS</Badge>
                </Group>
                <Text size="xl" fw={700}>
                  {stats.sms.total}
                </Text>
                <Text size="xs" c="dimmed">
                  Total SMS
                </Text>
                <Progress.Root size="sm">
                  <Progress.Section value={(stats.sms.sent / stats.sms.total) * 100} color="green">
                    <Progress.Label>Sent: {stats.sms.sent}</Progress.Label>
                  </Progress.Section>
                  <Progress.Section value={(stats.sms.failed / stats.sms.total) * 100} color="red">
                    <Progress.Label>Failed: {stats.sms.failed}</Progress.Label>
                  </Progress.Section>
                </Progress.Root>
              </Stack>
            </Card>

            <Card withBorder padding="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <IconBellRinging size={24} color="grape" />
                  <Badge color="grape">Push</Badge>
                </Group>
                <Text size="xl" fw={700}>
                  {stats.push.totalSent}
                </Text>
                <Text size="xs" c="dimmed">
                  Total Push Notifications
                </Text>
                <Text size="sm" c="blue">
                  {stats.push.activeSubscriptions} active devices
                </Text>
              </Stack>
            </Card>
          </Group>
        )}

        {/* Main Content */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <div>
              <Text size="lg" fw={700}>
                Notification Center
              </Text>
              <Text size="sm" c="dimmed">
                Manage email, SMS, and push notifications
              </Text>
            </div>
            <Group>
              <Button
                variant="light"
                leftSection={<IconCheck size={18} />}
                onClick={() => testMutation.mutate('all')}
                loading={testMutation.isPending}
              >
                Test All
              </Button>
              <Button
                leftSection={<IconSend size={18} />}
                onClick={() => setSendModalOpened(true)}
              >
                Send Notification
              </Button>
            </Group>
          </Group>

          <Tabs defaultValue="logs">
            <Tabs.List>
              <Tabs.Tab value="logs" leftSection={<IconBell size={16} />}>
                Recent Notifications
              </Tabs.Tab>
              <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
                Settings
              </Tabs.Tab>
              <Tabs.Tab value="stats" leftSection={<IconChartBar size={16} />}>
                Statistics
              </Tabs.Tab>
            </Tabs.List>

            {/* Logs Tab */}
            <Tabs.Panel value="logs" pt="md">
              <Tabs defaultValue="all">
                <Tabs.List>
                  <Tabs.Tab value="all">All</Tabs.Tab>
                  <Tabs.Tab value="email">Email</Tabs.Tab>
                  <Tabs.Tab value="sms">SMS</Tabs.Tab>
                  <Tabs.Tab value="push">Push</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="all" pt="md">
                  <NotificationLogsTable logs={[...(logs.email || []), ...(logs.sms || []), ...(logs.push || [])]} />
                </Tabs.Panel>
                <Tabs.Panel value="email" pt="md">
                  <NotificationLogsTable logs={logs.email || []} type="email" />
                </Tabs.Panel>
                <Tabs.Panel value="sms" pt="md">
                  <NotificationLogsTable logs={logs.sms || []} type="sms" />
                </Tabs.Panel>
                <Tabs.Panel value="push" pt="md">
                  <NotificationLogsTable logs={logs.push || []} type="push" />
                </Tabs.Panel>
              </Tabs>
            </Tabs.Panel>

            {/* Settings Tab */}
            <Tabs.Panel value="settings" pt="md">
              <NotificationSettings />
            </Tabs.Panel>

            {/* Stats Tab */}
            <Tabs.Panel value="stats" pt="md">
              {stats && <NotificationStatsView stats={stats} />}
            </Tabs.Panel>
          </Tabs>
        </Card>
      </Stack>

      {/* Send Notification Modal */}
      <SendNotificationModal
        opened={sendModalOpened}
        onClose={() => setSendModalOpened(false)}
        type={notificationType}
      />
    </>
  );
}

interface NotificationLogsTableProps {
  logs: any[];
  type?: string;
}

function NotificationLogsTable({ logs, type }: NotificationLogsTableProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getTypeIcon = (logType: string) => {
    switch (logType) {
      case 'email':
        return <IconMail size={16} />;
      case 'sms':
        return <IconMessage size={16} />;
      case 'push':
        return <IconBellRinging size={16} />;
      default:
        return <IconBell size={16} />;
    }
  };

  if (logs.length === 0) {
    return (
      <Alert color="blue" icon={<IconBell size={16} />}>
        No notifications found
      </Alert>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          {!type && <Table.Th>Type</Table.Th>}
          <Table.Th>Recipient</Table.Th>
          <Table.Th>Content</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Sent At</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {logs.slice(0, 20).map((log) => (
          <Table.Tr key={log.id}>
            {!type && (
              <Table.Td>
                <Badge leftSection={getTypeIcon(log.type || type)} variant="light">
                  {log.type || type}
                </Badge>
              </Table.Td>
            )}
            <Table.Td>
              <Text size="sm">{log.recipient || log.to}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" lineClamp={1}>
                {log.subject || log.title || log.message}
              </Text>
            </Table.Td>
            <Table.Td>
              <Badge color={getStatusColor(log.status)} variant="filled">
                {log.status}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="xs" c="dimmed">
                {log.sentAt
                  ? new Date(log.sentAt).toLocaleString()
                  : 'Not sent'}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function NotificationSettings() {
  return (
    <Stack gap="md">
      <Alert color="blue">
        <Text size="sm">
          Notification preferences coming soon! Configure which events trigger notifications.
        </Text>
      </Alert>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>New Lead Notifications</Text>
          <Switch label="Email notification" defaultChecked />
          <Switch label="SMS notification" />
          <Switch label="Push notification" defaultChecked />
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>Lead Match Notifications</Text>
          <Switch label="Email notification" defaultChecked />
          <Switch label="SMS notification" />
          <Switch label="Push notification" defaultChecked />
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={500}>Integration Sync Notifications</Text>
          <Switch label="Email notification" defaultChecked />
          <Switch label="SMS notification" />
          <Switch label="Push notification" />
        </Stack>
      </Card>
    </Stack>
  );
}

function NotificationStatsView({ stats }: { stats: NotificationStats }) {
  return (
    <Stack gap="md">
      <Group grow>
        <Card withBorder padding="md">
          <Text size="sm" fw={500} mb="md">
            Email Statistics
          </Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">Total:</Text>
              <Text size="sm" fw={600}>
                {stats.email.total}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="green">
                Sent:
              </Text>
              <Text size="sm" fw={600} c="green">
                {stats.email.sent}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="red">
                Failed:
              </Text>
              <Text size="sm" fw={600} c="red">
                {stats.email.failed}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="yellow">
                Pending:
              </Text>
              <Text size="sm" fw={600} c="yellow">
                {stats.email.pending}
              </Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder padding="md">
          <Text size="sm" fw={500} mb="md">
            SMS Statistics
          </Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">Total:</Text>
              <Text size="sm" fw={600}>
                {stats.sms.total}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="green">
                Sent:
              </Text>
              <Text size="sm" fw={600} c="green">
                {stats.sms.sent}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="teal">
                Delivered:
              </Text>
              <Text size="sm" fw={600} c="teal">
                {stats.sms.delivered}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="red">
                Failed:
              </Text>
              <Text size="sm" fw={600} c="red">
                {stats.sms.failed}
              </Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder padding="md">
          <Text size="sm" fw={500} mb="md">
            Push Statistics
          </Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">Total Sent:</Text>
              <Text size="sm" fw={600}>
                {stats.push.totalSent}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="red">
                Failed:
              </Text>
              <Text size="sm" fw={600} c="red">
                {stats.push.totalFailed}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="blue">
                Active Devices:
              </Text>
              <Text size="sm" fw={600} c="blue">
                {stats.push.activeSubscriptions}
              </Text>
            </Group>
          </Stack>
        </Card>
      </Group>
    </Stack>
  );
}

interface SendNotificationModalProps {
  opened: boolean;
  onClose: () => void;
  type: 'email' | 'sms' | 'push';
}

function SendNotificationModal({ opened, onClose, type }: SendNotificationModalProps) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const queryClient = useQueryClient();

  const sendMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/notifications/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });
      return response.json();
    },
    onSuccess: () => {
      mantineNotifications.show({
        title: 'Notification Sent',
        message: `${type} notification sent successfully`,
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['notification-logs'] });
      onClose();
      setTo('');
      setSubject('');
      setMessage('');
    },
  });

  const handleSend = () => {
    const data: any = { to, message };
    if (type === 'email') {
      data.subject = subject;
      data.html = `<p>${message}</p>`;
    }
    if (type === 'push') {
      data.title = subject;
    }
    sendMutation.mutate(data);
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Send ${type} Notification`}>
      <Stack gap="md">
        <TextInput
          label={type === 'sms' ? 'Phone Number' : type === 'email' ? 'Email' : 'User'}
          placeholder={type === 'sms' ? '+1234567890' : type === 'email' ? 'user@example.com' : 'User ID or Email'}
          value={to}
          onChange={(e) => setTo(e.currentTarget.value)}
        />

        {(type === 'email' || type === 'push') && (
          <TextInput
            label={type === 'email' ? 'Subject' : 'Title'}
            placeholder="Enter subject/title"
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
          />
        )}

        <Textarea
          label="Message"
          placeholder="Enter your message"
          minRows={4}
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
        />

        <Group justify="flex-end">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconSend size={16} />}
            onClick={handleSend}
            loading={sendMutation.isPending}
          >
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
